package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.components.motion.Collapsible
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.sddamico.api.apiGet
import com.sddamico.models.PlayerScoringResponse
import com.sddamico.models.RosterPlayerResponse
import com.sddamico.util.formatPoints
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun PlayerCard(
    player: RosterPlayerResponse,
    leagueYearId: String,
    onActivateLiv: (() -> Unit)? = null,
    onDeactivateLiv: (() -> Unit)? = null,
    onTrade: (() -> Unit)? = null,
) {
    val isLiv = player.rosterSlot == "LIV_LINE"
    val leftBorderColor = if (isLiv) SitePalette.error else SitePalette.border

    var expanded by remember { mutableStateOf(false) }
    var scoringData by remember { mutableStateOf<PlayerScoringResponse?>(null) }
    var scoringLoading by remember { mutableStateOf(false) }
    var scoringError by remember { mutableStateOf<String?>(null) }

    // Fetch scoring data on first expand
    LaunchedEffect(expanded) {
        if (expanded && scoringData == null && !scoringLoading) {
            scoringLoading = true
            scoringError = null
            try {
                scoringData = apiGet(
                    "/api/clubs/player-scoring?playerId=${player.id}&leagueYearId=$leagueYearId",
                    PlayerScoringResponse.serializer()
                )
            } catch (e: Exception) {
                scoringError = "Failed to load scoring data"
            }
            scoringLoading = false
        }
    }

    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, leftBorderColor)
            .borderRadius(8.px)
            .padding(1.cssRem)
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { expanded = !expanded }
            .transition(Transition.of("background-color", 150.ms, TransitionTimingFunction.Ease))
    ) {
        // Top row: name + sport badge + chevron
        Div(Modifier.fillMaxWidth().position(Position.Relative).toAttrs()) {
        Row(
            Modifier.fillMaxWidth().padding(bottom = 0.5.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                player.name,
                Modifier.fontSize(0.9375.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
            )
            if (player.age != null) {
                var ageHovered by remember { mutableStateOf(false) }
                Div(
                    Modifier
                        .position(Position.Relative)
                        .display(DisplayStyle.InlineBlock)
                        .onMouseEnter { ageHovered = true }
                        .onMouseLeave { ageHovered = false }
                        .toAttrs()
                ) {
                    SpanText(
                        "  ${player.age}",
                        Modifier
                            .fontSize(0.9375.cssRem)
                            .fontStyle(FontStyle.Italic)
                            .color(SitePalette.textMuted)
                    )
                    if (ageHovered && player.birthDate != null) {
                        Div(
                            Modifier
                                .position(Position.Absolute)
                                .styleModifier {
                                    property("bottom", "100%")
                                    property("left", "50%")
                                    property("transform", "translateX(-50%)")
                                    property("white-space", "nowrap")
                                    property("z-index", "10")
                                }
                                .backgroundColor(SitePalette.surface)
                                .border(1.px, LineStyle.Solid, SitePalette.border)
                                .borderRadius(6.px)
                                .padding(topBottom = 0.25.cssRem, leftRight = 0.5.cssRem)
                                .margin(bottom = 0.25.cssRem)
                                .fontSize(0.6875.cssRem)
                                .color(SitePalette.textSecondary)
                                .toAttrs()
                        ) {
                            Text(formatBirthDate(player.birthDate))
                        }
                    }
                }
            }
            Spacer()
            if (onTrade != null) {
                Div(
                    Modifier
                        .size(1.625.cssRem)
                        .borderRadius(9999.px)
                        .border(1.5.px, LineStyle.Solid, SitePalette.accent)
                        .display(DisplayStyle.Flex)
                        .styleModifier {
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                        .cursor(Cursor.Pointer)
                        .margin(right = 0.5.cssRem)
                        .onClick { it.stopPropagation(); onTrade() }
                        .toAttrs()
                ) {
                    SpanText("⇄", Modifier.fontSize(0.8125.cssRem).color(SitePalette.accent))
                }
                Div(
                    Modifier
                        .styleModifier { property("transform", "scale(1.2)"); property("transform-origin", "center") }
                        .toAttrs()
                ) {
                    SportBadge(player.sport, player.isLiv)
                }
            } else {
                SportBadge(player.sport, player.isLiv)
            }
            // Chevron indicator
            ExpandChevron(expanded, Modifier.fontSize(0.625.cssRem).margin(left = 0.5.cssRem))
        }
        if (player.rankingLabel != null) {
            Div(
                Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "100%")
                        property("right", "1.125rem")
                    }
                    .display(DisplayStyle.Flex)
                    .alignItems(AlignItems.Center)
                    .toAttrs()
            ) {
                SpanText(
                    "${player.rankingLabel}: ",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
                SpanText(
                    "#${player.rankingValue}",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.text)
                )
            }
        }
        } // end relative Div

        // Season points
        Row(verticalAlignment = Alignment.Bottom) {
            SpanText(
                formatPoints(player.seasonPoints),
                Modifier
                    .fontSize(1.5.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.accent)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") }
            )
            if (player.provisionalPoints > 0) {
                SpanText(
                    "+${formatPoints(player.provisionalPoints)} pending",
                    Modifier
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .fontStyle(FontStyle.Italic)
                        .color(SitePalette.textMuted)
                        .margin(left = 0.5.cssRem)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                )
            }
        }
        SpanText("season pts", Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted))

        // Active tournament info
        val activeTournament = player.activeTournament
        if (activeTournament != null) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .margin(top = 0.75.cssRem)
                    .padding(top = 0.75.cssRem)
                    .borderTop(1.px, LineStyle.Solid, SitePalette.border)
            ) {
                SpanText(
                    activeTournament.tournamentName,
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textSecondary)
                )
                Row(
                    Modifier.fillMaxWidth().margin(top = 0.25.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val prefix = standingPrefix(player.sport, activeTournament.standing)
                    if (prefix != null) {
                        SpanText(
                            prefix,
                            Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted).margin(right = 0.25.cssRem)
                        )
                    }
                    SpanText(
                        activeTournament.standing,
                        Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.Medium).color(SitePalette.text)
                    )
                    Spacer()
                    SpanText(
                        "+${formatPoints(activeTournament.provisionalPoints)} pts",
                        Modifier
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .color(SitePalette.primary)
                    )
                }
            }
        } else if (!isLiv) {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    "No active tournament",
                    Modifier
                        .fontSize(0.75.cssRem)
                        .color(SitePalette.textMuted)
                )
                if (player.isLiv && onDeactivateLiv != null) {
                    Spacer()
                    Div(
                        Modifier
                            .backgroundColor(SitePalette.error)
                            .color(SitePalette.background)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.25.cssRem, leftRight = 0.625.cssRem)
                            .fontSize(0.6875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(Cursor.Pointer)
                            .onClick { it.stopPropagation(); onDeactivateLiv() }
                            .toAttrs()
                    ) {
                        Text("To LIV Line")
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    "LIV Line (inactive)",
                    Modifier
                        .fontSize(0.75.cssRem)
                        .color(SitePalette.error)
                )
                if (onActivateLiv != null) {
                    Spacer()
                    Div(
                        Modifier
                            .backgroundColor(SitePalette.error)
                            .color(SitePalette.background)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.25.cssRem, leftRight = 0.625.cssRem)
                            .fontSize(0.6875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(Cursor.Pointer)
                            .onClick { it.stopPropagation(); onActivateLiv() }
                            .toAttrs()
                    ) {
                        Text("Activate")
                    }
                }
            }
        }

        Collapsible(expanded) {
            ScoringSummary(scoringLoading, scoringError, scoringData, topSpacing = 0.75.cssRem)
        }
    }
}

@Composable
internal fun ScoringSummary(
    loading: Boolean,
    error: String?,
    data: PlayerScoringResponse?,
    topSpacing: CSSLengthOrPercentageNumericValue,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .margin(top = topSpacing)
            .padding(top = topSpacing)
            .borderTop(1.px, LineStyle.Solid, SitePalette.border)
    ) {
        SpanText(
            "Scoring Summary",
            Modifier
                .fontSize(0.8125.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textSecondary)
                .margin(bottom = 0.5.cssRem)
        )

        when {
            loading -> Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                repeat(3) { SkeletonBlock(2.5.cssRem) }
            }
            error != null -> SpanText(
                error,
                Modifier.fontSize(0.75.cssRem).color(SitePalette.error)
            )
            data != null && data.entries.isEmpty() -> SpanText(
                "No tournament results",
                Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
            )
            data != null -> {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .gap(0.375.cssRem)
                        .animation(FadeInKeyframes.toAnimation(Durations.ENTER.ms, Ease.Out.fn))
                ) {
                    data.entries.forEach { entry ->
                        ScoringEntry(entry)
                    }
                }
            }
        }
    }
}

/** Points down when collapsed and turns over when expanded. */
@Composable
internal fun ExpandChevron(expanded: Boolean, modifier: Modifier = Modifier) {
    SpanText(
        "\u25BC",
        modifier
            .color(SitePalette.textMuted)
            .rotate(if (expanded) 180.deg else 0.deg)
            .transition(Transition.of("transform", Durations.MOVE.ms, Ease.Out.fn))
    )
}

@Composable
internal fun ScoringEntry(entry: com.sddamico.models.ScoringTournamentEntry) {
    val textColor = if (entry.provisional) SitePalette.textMuted else SitePalette.text
    val pointsColor = if (entry.provisional) SitePalette.textMuted else SitePalette.accent
    val fontStyle = if (entry.provisional) FontStyle.Italic else FontStyle.Normal

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.background)
            .borderRadius(6.px)
            .padding(0.5.cssRem, 0.625.cssRem)
    ) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                entry.tournamentName,
                Modifier
                    .fontSize(0.75.cssRem)
                    .fontWeight(FontWeight.Medium)
                    .color(textColor)
                    .fontStyle(fontStyle)
            )
            Spacer()
            if (entry.importance != "NORMAL") {
                ImportanceBadge(entry.importance)
            }
        }
        Row(
            Modifier.fillMaxWidth().margin(top = 0.2.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                entry.standing,
                Modifier.fontSize(0.6875.cssRem).color(SitePalette.textSecondary).fontStyle(fontStyle)
            )
            if (entry.provisional) {
                SpanText(
                    " (provisional)",
                    Modifier.fontSize(0.625.cssRem).color(SitePalette.textMuted).fontStyle(FontStyle.Italic)
                )
            }
            Spacer()
            SpanText(
                "${formatPoints(entry.points)} pts",
                Modifier
                    .fontSize(0.8125.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(pointsColor)
                    .fontStyle(fontStyle)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") }
            )
        }
    }
}

private fun standingPrefix(sport: String, standing: String): String? {
    return when (sport) {
        "MENS_GOLF" -> "POS:"
        "MENS_TENNIS", "WOMENS_TENNIS" -> {
            if (standing == "Winner" || standing == "Final") null
            else "ROUND:"
        }
        else -> null
    }
}

private val MONTH_NAMES = arrayOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
)

/** Formats "1998-08-23" to "Born: Aug 23, 1998" */
private fun formatBirthDate(isoDate: String): String {
    val parts = isoDate.split("-")
    if (parts.size != 3) return "Born: $isoDate"
    val year = parts[0]
    val month = parts[1].toIntOrNull()?.let { MONTH_NAMES.getOrNull(it - 1) } ?: parts[1]
    val day = parts[2].toIntOrNull()?.toString() ?: parts[2]
    return "Born: $month $day, $year"
}

@Composable
internal fun ImportanceBadge(importance: String) {
    val (label, bgColor) = when (importance) {
        "MAJOR" -> "Major" to SitePalette.accent
        "SPECIAL" -> "Special" to SitePalette.primary
        else -> return
    }
    Div(
        Modifier
            .backgroundColor(bgColor)
            .borderRadius(9999.px)
            .padding(topBottom = 0.1.cssRem, leftRight = 0.4.cssRem)
            .fontSize(0.5625.cssRem)
            .fontWeight(600)
            .color(SitePalette.background)
            .lineHeight(1.4)
            .toAttrs()
    ) {
        Text(label)
    }
}
